import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { MessageSquare, Phone, Shield, Users, Check, ArrowUpRight, ShieldCheck, MessageCircle, Bell, Globe, Zap, Users as UsersIcon, ChevronDown, ChevronUp, Star, Lock, Smartphone, Award, Search, Clock, FileText, TrendingUp, Target, BarChart, MapPin, Globe2, Building2, Store, ShoppingCart, HeadphonesIcon, Stethoscope, GraduationCap, Truck, Coffee, Sparkles, Clock1, Send, Users2, Bot, CheckCircle2, AlertCircle, Info, Mail, Code, Globe as World, Calendar, Megaphone, Zap as Lightning, Workflow, Play, Image as ImageIcon, FileText as Document, Layers, Archive, CheckCheck, Zap as Flash, ShieldAlert, Clock3, Target as TargetIcon, BarChart2, Settings } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: MessageCircle, title: 'WhatsApp Business API & Cloud API', text: 'Official Meta-approved WhatsApp Business API and Cloud API integration. Reliable, scalable, and compliant messaging for businesses of all sizes across Africa.' },
  { icon: Bot, title: 'Message templates & automation', text: 'Use pre-approved message templates for notifications, alerts, and marketing messages. Automate responses with AI-powered Zyra Agent for 24/7 customer support.' },
  { icon: Users2, title: 'Customer opt-in management', text: 'WhatsApp requires customer opt-in for business messaging. Our system helps you manage opt-ins, maintain compliance, and deliver relevant messages only to interested customers.' },
  { icon: ShieldCheck, title: 'Enterprise security & compliance', text: 'WhatsApp Business API is secure and compliant with global regulations. End-to-end encryption, data protection, and workspace isolation for business communication.' },
  { icon: Bell, title: 'Real-time notifications', text: 'Receive instant notifications when customers message your WhatsApp number. Never miss a customer inquiry with push notifications and desktop alerts.' },
  { icon: Layers, title: 'Unified inbox integration', text: 'WhatsApp messages flow into your unified inbox alongside email, ZuriChat, and other channels. Manage all customer communications from one centralized dashboard.' },
];

const benefits = [
  { title: 'Reach 2 billion+ WhatsApp users', text: 'Connect with customers on the global messaging platform. WhatsApp has over 2 billion users worldwide.' },
  { title: 'Official Meta-approved integration', text: 'Our WhatsApp Business API integration is officially approved by Meta. No third-party intermediaries, direct access to WhatsApp infrastructure, and reliable message delivery.' },
  { title: 'Template-based messaging for outreach', text: 'Send marketing messages, notifications, and updates using pre-approved WhatsApp message templates. Ensure compliance while reaching customers proactively.' },
  { title: 'Team collaboration on WhatsApp', text: 'Multiple agents can handle WhatsApp conversations with assignment, internal notes, escalation, and real-time collaboration. No need to share WhatsApp accounts.' },
];

const useCases = [
  { title: 'Order confirmations & shipping updates', text: 'Send automated order confirmations, shipping notifications, and delivery updates via WhatsApp templates. Keep customers informed throughout their purchase journey.' },
  { title: 'Appointment reminders & confirmations', text: 'Reduce no-shows with automated appointment reminders and confirmations. Healthcare, salons, consultants, and service providers benefit from timely reminders.' },
  { title: 'Customer support & inquiries', text: 'Handle customer inquiries, troubleshooting, and support via WhatsApp. Provide real-time assistance with multiple agents collaborating on conversations.' },
  { title: 'Marketing campaigns & promotions', text: 'Send targeted marketing messages, promotional offers, and product announcements to opted-in customers using WhatsApp message templates.' },
];

const industries = [
  { title: 'E-commerce & Retail', text: 'Send order updates, shipping notifications, return confirmations, and promotional offers globally. Reduce cart abandonment with timely WhatsApp reminders.' },
  { title: 'Healthcare & Medical', text: 'Send appointment reminders, prescription notifications, and follow-up messages worldwide. Provide secure communication while maintaining patient privacy and compliance.' },
  { title: 'Financial Services', text: 'Send transaction alerts, payment confirmations, fraud warnings, and account updates internationally. Secure, instant communication for banking and fintech customers.' },
  { title: 'Travel & Hospitality', text: 'Send booking confirmations, check-in reminders, travel updates, and promotional offers globally. Enhance guest experience with real-time WhatsApp communication.' },
];

const pricing = [
  { tier: 'Integration', price: 'No platform fees', features: ['Free to integrate', 'Simple connection via WABA ID, Number ID, and Access Token', 'Embedded signup option', 'Message templates', 'Basic automation', '24/5 customer support'] },
  { tier: 'Message Costs', price: 'Meta billing only', features: ['No markup on WhatsApp conversations', 'Pay Meta directly for conversations', 'Transparent Meta pricing', 'No hidden platform fees', 'Unlimited conversations', 'Analytics & reporting'] },
  { tier: 'Premium Features', price: 'Optional add-ons', features: ['Zyra AI integration', 'Advanced automation', '24/7 Enterprise support', 'Multiple WhatsApp numbers', 'Custom integrations', 'White-label options'] },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is WhatsApp Business API?',
    answer: 'WhatsApp Business API is an official Meta-approved API that allows businesses to send and receive messages at scale programmatically. Unlike regular WhatsApp, the API supports automation, templates, multiple agents, and integration with business systems. ZuriHub provides a seamless integration with WhatsApp Business API through our unified inbox.',
  },
  {
    question: 'How do I integrate WhatsApp Business API?',
    answer: 'Integration is simple. You can connect your WhatsApp Business account using your WABA ID, Number ID, and Access Token. We also offer an embedded signup option for easier onboarding. No webhook configuration is required on your end - we handle all the technical setup automatically.',
  },
  {
    question: 'What are WhatsApp message templates?',
    answer: 'WhatsApp message templates are pre-approved message formats for sending notifications, alerts, and marketing messages to customers. Templates are required for any outbound business-initiated messaging. Examples include order confirmations, appointment reminders, shipping updates, and promotional offers. Templates must be approved by Meta before use.',
  },
  {
    question: 'Can I send marketing messages on WhatsApp?',
    answer: 'Yes, but only to customers who have opted in to receive marketing messages from your business. All marketing messages must use pre-approved WhatsApp message templates. Customer-initiated conversations do not require templates. WhatsApp enforces strict anti-spam policies to maintain platform quality.',
  },
  {
    question: 'How much does WhatsApp Business API cost?',
    answer: 'ZuriHub does not charge platform fees for WhatsApp integration. All message costs are billed directly by Meta to your Meta Business account. You pay Meta for conversations based on their standard pricing. This ensures transparent, direct billing with no markup or hidden fees.',
  },
  {
    question: 'What countries does WhatsApp Business API support?',
    answer: 'WhatsApp Business API is available globally, supporting over 180 countries. ZuriHub supports WhatsApp messaging for businesses worldwide. The same API works across all regions.',
  },
  {
    question: 'How do I get WhatsApp Business API access?',
    answer: 'To access WhatsApp Business API, you need a verified Meta Business account and an approved phone number. You can use our embedded signup to get started quickly, or connect an existing WhatsApp Business account using your WABA ID, Number ID, and Access Token. We handle the technical integration.',
  },
  {
    question: 'Can multiple agents handle WhatsApp conversations?',
    answer: 'Yes, unlike regular WhatsApp limited to one device, WhatsApp Business API through ZuriHub allows multiple agents to handle conversations simultaneously. Conversations are routed, assigned, and managed in your unified inbox. Agents can collaborate with internal notes and escalation.',
  },
  {
    question: 'Is WhatsApp Business API secure?',
    answer: 'Yes, WhatsApp Business API is secure with end-to-end encryption for all messages. Meta\'s infrastructure is enterprise-grade with data protection, security compliance, and reliability. ZuriHub adds workspace isolation, access controls, and additional security layers for business compliance.',
  },
  {
    question: 'Do I need to configure webhooks?',
    answer: 'No, you do not need to configure webhooks. We handle all webhook configuration and message routing automatically. Simply connect your WhatsApp Business account using your credentials, and we manage the technical integration for you.',
  },
  {
    question: 'Can I use WhatsApp for customer support?',
    answer: 'Yes, WhatsApp is excellent for customer support. Customers can initiate conversations, and your agents respond in real-time. WhatsApp\'s high open rates (98%) and quick response times make it ideal for support. We provide full 24/5 customer support and 24/7 Enterprise support for all plans.',
  },
  {
    question: 'How does WhatsApp integrate with other channels?',
    answer: 'WhatsApp messages flow into your ZuriHub unified inbox alongside email, ZuriChat, and other channels. Customer history is unified across all channels. Agents can switch between channels seamlessly. Automation and workflows work consistently across WhatsApp and other platforms.',
  },
  {
    question: 'What is the difference between WABA ID and Number ID?',
    answer: 'WABA ID (WhatsApp Business Account ID) identifies your business account, while Number ID identifies a specific phone number within that account. You need both credentials along with an Access Token to connect your WhatsApp Business account to ZuriHub. Our embedded signup simplifies this process.',
  },
];

export default function ChannelsWhatsApp() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="WhatsApp Business API Integration - Reach 2 Billion Customers | Official Meta API"
        description="Official Meta-approved WhatsApp Business API integration for businesses. Reach 2 billion customers globally with message templates, automation, unified inbox, and team collaboration."
        path="/channels/whatsapp"
        keywords="WhatsApp Business API, WhatsApp Cloud API, WhatsApp for business, Meta WhatsApp API, customer messaging, WhatsApp automation, WhatsApp templates, global WhatsApp business, enterprise WhatsApp"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-4 py-1.5 text-sm font-semibold text-green-300 backdrop-blur">
                <CheckCircle2 className="h-4 w-4" /> Official Meta-approved API
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Reach 2 billion customers on <span className="gradient-text">WhatsApp</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Official Meta-approved WhatsApp Business API integration. Connect with customers globally with message templates, automation, and unified inbox.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-green-500 hover:bg-slate-800"
                >
                  View Pricing <BarChart2 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-green-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/WhatsApp%20Header.png"
                  alt="WhatsApp Business API integration showing unified inbox and customer conversations"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Enterprise WhatsApp Business API features</h2>
            <p className="section-subtitle mx-auto mt-4">Official Meta-approved integration with enterprise-grade features for African businesses.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-green-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why businesses choose WhatsApp Business API</h2>
            <p className="section-subtitle mx-auto mt-4">Advantages that set our WhatsApp integration apart from regular WhatsApp accounts.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {benefits.map((b) => (
              <div key={b.title} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-100 text-green-600">
                  <Check className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="use-cases-heading" className="section-title">WhatsApp use cases for your business</h2>
            <p className="section-subtitle mx-auto mt-4">Practical applications of WhatsApp Business API across different business scenarios.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-green-600">
                  <Megaphone className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Industries powered by WhatsApp</h2>
            <p className="section-subtitle mx-auto mt-4">From e-commerce to healthcare, businesses across all industries use WhatsApp.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((i) => (
              <div key={i.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-green-600">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{i.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="pricing-heading" className="section-title">Simple integration, no platform fees</h2>
            <p className="section-subtitle mx-auto mt-4">Connect via WABA ID, Number ID, and Access Token. All message costs go directly to Meta.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {pricing.map((p) => (
              <div key={p.tier} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900">{p.tier}</h3>
                <p className="mt-2 text-3xl font-extrabold text-slate-900">{p.price}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 block w-full rounded-full bg-slate-900 px-6 py-3 text-center text-base font-semibold text-white transition hover:bg-slate-800"
                >
                  Connect WhatsApp
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about WhatsApp Business API integration.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-slate-200 bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="text-base font-semibold text-slate-900">{faq.question}</span>
                  {openFaq === faq.question ? (
                    <ChevronUp className="h-5 w-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-500" />
                  )}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to reach 2 billion customers on WhatsApp?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Start your free trial today with 1,000 free conversations monthly. Official Meta-approved WhatsApp Business API integration.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Get Started Free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}